import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import FolderPermissionBlock from '../components/FolderPermissionBlock';
import { createAgent } from '@/api/agent';
import { getTree, type CategoryNode } from '@/api/materialCategory';
import { listMaterial, saveConfidentialAgents, type MaterialItem } from '@/api/materialInfo';

/** 文件类型对应缩略图占位图标 */
const typeThumbnailConfig: Record<string, { icon: string; color: string; bg: string }> = {
  Video: { icon: 'ri-video-line',        color: '#6B7280', bg: '#F3F4F6' },
  PDF:   { icon: 'ri-file-pdf-2-line',   color: '#DC2626', bg: '#FEF2F2' },
  PPT:   { icon: 'ri-file-ppt-2-line',   color: '#D04423', bg: '#FFF0ED' },
  Excel: { icon: 'ri-file-excel-2-line', color: '#1E7E45', bg: '#EDFAF2' },
  Word:  { icon: 'ri-file-word-2-line',  color: '#1A5CA8', bg: '#EDF3FC' },
};

const regions = [
  'Europe', 'North America', 'South America', 'Middle East',
  'Southeast Asia', 'South Asia', 'East Asia', 'Africa', 'Oceania',
];

function generatePassword(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

interface CreateForm {
  name: string;
  email: string;
  company: string;
  region: string;
  password: string;
  expiryDays: string;
  notes: string;
}

export default function AgentCreatePage() {
  const navigate = useNavigate();

  // 机密资料：从后端加载
  const [confMaterials, setConfMaterials] = useState<MaterialItem[]>([]);
  const [confMaterialsLoading, setConfMaterialsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await listMaterial({ confidential: 1, pageSize: 999 });
        if (!cancelled) {
          setConfMaterials(result.list);
          setConfMaterialsLoading(false);
        }
      } catch {
        if (!cancelled) setConfMaterialsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [form, setForm] = useState<CreateForm>({
    name: '',
    email: '',
    company: '',
    region: '',
    password: '',
    expiryDays: '365',
    notes: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [newUserModels, setNewUserModels] = useState<string[] | undefined>(undefined);
  const [confGrants, setConfGrants] = useState<Record<string, boolean>>({});
  const [createSuccess, setCreateSuccess] = useState<{ name: string; email: string; password: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);

  useEffect(() => {
    getTree().then(setCategoryTree).catch(() => {});
  }, []);


  const expiryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + parseInt(form.expiryDays, 10));
    return d.toISOString().slice(0, 10);
  })();

  const grantedConfCount = Object.values(confGrants).filter(Boolean).length;

  // 记录加载失败的缩略图 ID
  const [brokenThumbnails, setBrokenThumbnails] = useState<Set<string>>(new Set());
  const handleThumbnailError = (id: string) => {
    setBrokenThumbnails((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    setLoading(true);
    try {
      const newAgent = await createAgent({
        name: form.name,
        email: form.email,
        company: form.company,
        region: form.region,
        password: form.password,
        expiryDays: form.expiryDays,
        notes: form.notes || undefined,
        modelIds: !newUserModels || newUserModels.length === 0 ? '' : newUserModels.join(','),
      });

      // 保存机密资料授权
      const newAgentId = newAgent.id;
      const grantedMaterialIds = Object.entries(confGrants)
        .filter(([, granted]) => granted)
        .map(([id]) => id);
      if (grantedMaterialIds.length > 0) {
        await Promise.all(
          grantedMaterialIds.map((matId) => {
            const mat = confMaterials.find((m) => m.id === matId);
            const currentAgents = mat?.confidentialAgents ?? [];
            return saveConfidentialAgents(matId, [...currentAgents, newAgentId]);
          })
        );
      }

      setCreateSuccess({ name: form.name, email: form.email, password: form.password });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "创建失败，请重试";
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400';

  return (
    <AdminLayout title="新建代理商" subtitle="手动录入代理商信息，账号创建后立即生效">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/admin/agents')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base"></i>
          返回代理商列表
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-3 gap-6">
          {/* Left sidebar: summary */}
          <div className="col-span-1 space-y-4">
            {/* Preview card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">账号预览</p>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-base font-bold shrink-0" style={{ background: '#0069BD' }}>
                  {form.name
                    ? form.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
                    : <i className="ri-user-line text-lg"></i>}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{form.name || <span className="text-gray-300">姓名</span>}</p>
                  <p className="text-xs text-gray-400 truncate">{form.email || <span className="text-gray-300">邮箱</span>}</p>
                  <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-green-50 text-green-700 border-green-200">
                    启用中
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <i className="ri-building-line text-xs w-4 text-center text-gray-400"></i>
                  <span className="truncate">{form.company || <span className="text-gray-300">公司名称</span>}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <i className="ri-map-pin-line text-xs w-4 text-center text-gray-400"></i>
                  <span>{form.region || <span className="text-gray-300">所在地区</span>}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <i className="ri-calendar-line text-xs w-4 text-center text-gray-400"></i>
                  <span>有效至 <strong className="text-gray-700">{expiryDate}</strong></span>
                </div>
              </div>
            </div>

            {/* Permission summary */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">权限摘要</p>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-gray-500">
                    <i className="ri-layout-grid-line text-xs text-gray-400"></i>
                    分类权限
                  </span>
                  <span className={`text-xs font-semibold ${!newUserModels || newUserModels.length === 0 ? 'text-green-600' : 'text-gray-700'}`}>
                    {!newUserModels || newUserModels.length === 0 ? '全部可见' : `${newUserModels.length} 个型号`}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-gray-500">
                    <i className="ri-shield-keyhole-line text-xs text-gray-400"></i>
                    机密资料
                  </span>
                  <span className="text-xs font-semibold text-gray-700">
                    {grantedConfCount > 0 ? `已授权 ${grantedConfCount} 份` : '未授权'}
                  </span>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-500 text-sm"></i>
              </div>
              <p className="text-xs text-amber-700 leading-relaxed">账号创建后立即生效，请通过邮件或其他安全渠道将登录凭据发送给用户。</p>
            </div>
          </div>

          {/* Right: form sections */}
          <div className="col-span-2 space-y-5">

            {/* Basic info */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-gray-400"></i>基本信息
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">姓名 *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="请输入姓名"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">邮箱地址 *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="agent@company.com"
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">公司名称 *</label>
                    <input
                      type="text"
                      required
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      placeholder="英文公司名称"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">所在地区 *</label>
                    <div className="relative">
                      <select
                        required
                        value={form.region}
                        onChange={(e) => setForm({ ...form, region: e.target.value })}
                        className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none bg-white cursor-pointer focus:border-gray-400"
                      >
                        <option value="">选择地区</option>
                        {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Expiry */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <i className="ri-calendar-check-line text-gray-400"></i>授权时间
              </h3>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">账号有效期</label>
                <div className="relative">
                  <select
                    value={form.expiryDays}
                    onChange={(e) => setForm({ ...form, expiryDays: e.target.value })}
                    className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none bg-white cursor-pointer focus:border-gray-400"
                  >
                    <option value="90">90 天</option>
                    <option value="180">180 天</option>
                    <option value="365">1 年</option>
                    <option value="730">2 年</option>
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  有效期至：<strong className="text-gray-600">{expiryDate}</strong>
                </p>
              </div>
            </div>

            {/* Folder permissions */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <i className="ri-layout-grid-line text-gray-400"></i>资料分类权限
                </h3>
                <span className="text-xs text-gray-400">按叶子分类授权</span>
              </div>
              <FolderPermissionBlock
                categoryTree={categoryTree}
                allowedModels={newUserModels}
                onChange={setNewUserModels}
              />
              <div className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(5,150,105,0.05)', border: '1px solid rgba(5,150,105,0.15)' }}>
                <div className="w-4 h-4 flex items-center justify-center shrink-0 text-green-600">
                  <i className="ri-global-line text-sm"></i>
                </div>
                <p className="text-xs text-green-700 leading-relaxed">
                  <strong>普通资料</strong>：在可见文件夹范围内，代理商可访问全部普通资料。
                </p>
              </div>
            </div>

            {/* Confidential materials */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <i className="ri-shield-keyhole-line text-gray-400"></i>机密资料授权
                </h3>
                <span className="text-xs text-gray-400">已授权 {grantedConfCount} / {confMaterials.length} 份</span>
              </div>
              {confMaterialsLoading ? (
                <div className="py-8 text-center text-gray-400">
                  <i className="ri-loader-4-line text-2xl mb-2 block animate-spin"></i>
                  <p className="text-sm">加载机密资料...</p>
                </div>
              ) : confMaterials.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-4 text-center">暂无机密资料</p>
              ) : (
                <div className="space-y-2">
                  {confMaterials.map((mat) => {
                    const granted = confGrants[mat.id] ?? false;
                    return (
                      <label
                        key={mat.id}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${granted ? 'border-red-200 bg-red-50/30' : 'border-gray-100 bg-gray-50/50 hover:bg-gray-100/60'}`}
                      >
                        <input
                          type="checkbox"
                          checked={granted}
                          onChange={() => setConfGrants((prev) => ({ ...prev, [mat.id]: !prev[mat.id] }))}
                          className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-red-500"
                        />
                        <div className="w-12 h-8 rounded overflow-hidden bg-gray-100 shrink-0 relative">
                          {(() => {
                            const showPlaceholder = !mat.thumbnail || brokenThumbnails.has(mat.id);
                            if (showPlaceholder) {
                              const cfg = typeThumbnailConfig[mat.type] ?? typeThumbnailConfig.PDF;
                              return (
                                <div className="w-full h-full flex items-center justify-center" style={{ background: cfg.bg }}>
                                  <i className={cfg.icon} style={{ color: cfg.color, fontSize: '16px' }}></i>
                                </div>
                              );
                            }
                            return (
                              <img
                                src={mat.thumbnail}
                                alt={mat.title}
                                className="w-full h-full object-cover"
                                onError={() => handleThumbnailError(mat.id)}
                              />
                            );
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{mat.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded-full text-gray-500">{mat.category}</span>
                            <span className="text-xs text-gray-400">{mat.type}</span>
                          </div>
                        </div>
                        {granted && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600 shrink-0 whitespace-nowrap">已授权</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Login credentials */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <i className="ri-lock-password-line text-gray-400"></i>登录凭据
              </h3>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">初始密码 *</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, password: generatePassword() })}
                    className="text-xs font-semibold cursor-pointer whitespace-nowrap hover:opacity-80 transition-opacity"
                    style={{ color: '#0069BD' }}
                  >
                    随机生成
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="设置一个强密码"
                    className="w-full pr-10 px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPw ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'}></i>
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">请通过安全的线下渠道将密码告知用户。</p>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <i className="ri-sticky-note-line text-gray-400"></i>备注
              </h3>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="可选，填写此账号的备注信息，例如：跟进事项、合作背景等（仅管理员可见）"
                rows={4}
                maxLength={500}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{form.notes.length}/500</p>
            </div>

            {/* Bottom action */}
            {apiError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <i className="ri-error-warning-line text-base shrink-0"></i>
                <span>{apiError}</span>
              </div>
            )}
            <div className="flex gap-3 pb-6">
              <button
                type="button"
                onClick={() => navigate('/admin/agents')}
                disabled={loading}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: '#0069BD' }}
              >
                {loading && <i className="ri-loader-4-line text-base animate-spin"></i>}
                {loading ? '创建中...' : '创建账号'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Success modal */}
      {createSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(0,105,189,0.08)' }}>
              <i className="ri-user-follow-line text-2xl" style={{ color: '#0069BD' }}></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">账号已创建</h3>
            <p className="text-sm text-gray-500 mb-5">请通过安全渠道将以下登录凭据发送给用户。</p>
            <div className="bg-gray-50 rounded-xl p-4 text-left space-y-3 mb-5">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">姓名</p>
                <p className="text-sm font-semibold text-gray-800">{createSuccess.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">邮箱</p>
                <p className="text-sm font-semibold text-gray-800">{createSuccess.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">密码</p>
                <p className="text-sm font-mono font-bold text-gray-900 bg-white border border-gray-200 rounded-lg px-3 py-2 select-all">{createSuccess.password}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/agents')}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: '#0069BD' }}
            >
              完成，返回列表
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
